/**
 * Desplegable para elegir canal (P9).
 *
 * Es el mismo que abre el "+" de Conversaciones, y vive acá para que **sea el mismo código** en los
 * dos lados. Antes compartir un presupuesto abría un modal aparte ("Compartir por WhatsApp", con
 * Select y botón Continuar) para preguntar exactamente lo mismo: dos UX distintas para una decisión
 * de un solo click.
 *
 * Se le pasan los canales ya resueltos porque cada lado tiene su propio criterio: la bandeja lista
 * los accesibles del store; compartir lista sólo los **escribibles** (ver `useWritableChannels`), y de
 * la Cloud API, sólo los que tienen la ventana de 24 h abierta con ese número.
 */
import React, { useCallback, useRef, useState, type ReactElement } from "react";
import { Dropdown, Flex } from "antd";
import { MailOutlined, MessageOutlined, PlusOutlined } from "@ant-design/icons";
import { WhatsAppMetaIcon, WhatsAppQrIcon } from "@/components/icon/WhatsAppChannelIcons";
import {
  Channel,
  conversationsApi,
  isConversationsConfigured,
} from "@/services/conversations.api";
import { isCloudApi, isWhatsappFamily, ofreciblesPara } from "@/services/channel-types";

const ICON_SIZE = 16;
const ITEM_STYLE = { padding: "4px 0", minWidth: 220 } as const;

export const channelIcon = (type: string) => {
  if (isCloudApi(type)) return <WhatsAppMetaIcon style={{ fontSize: ICON_SIZE }} />;
  if (isWhatsappFamily(type)) return <WhatsAppQrIcon style={{ fontSize: ICON_SIZE }} />;
  if (type === "email") return <MailOutlined style={{ fontSize: ICON_SIZE }} />;
  return <MessageOutlined style={{ fontSize: ICON_SIZE }} />;
};

/**
 * `identifier` ya viene con el `+` del prefijo internacional (`+543517733850`), así que agregarle
 * otro mostraba `canal test ++543517733850`.
 */
export const channelLabel = (ch: Channel): string =>
  ch.identifier ? `${ch.name} ${ch.identifier}` : ch.name;

type ExtraItem = { key: string; label: React.ReactNode; onClick: () => void };

/**
 * Los canales de la Cloud API que no se ofrecen. Sólo se dice que no están: el motivo largo de la
 * ventana de 24 h ocupaba medio desplegable, y donde importa es en el chat, que lo avisa al entrar.
 */
const notaSinVentana = (sinVentana: Channel[]) => ({
  key: "__sin_ventana",
  disabled: true,
  label: (
    <span style={{ ...ITEM_STYLE, display: "block", maxWidth: 280, whiteSpace: "normal" }}>
      {sinVentana.map((ch) => `«${ch.name}»`).join(", ")}{" "}
      {sinVentana.length === 1 ? "no está disponible" : "no están disponibles"}
    </span>
  ),
});

export const ChannelPickerDropdown = ({
  channels,
  loading,
  failed,
  emptyText = "No hay canales disponibles",
  onPick,
  onOpen,
  onAddChannel,
  extraItems,
  unavailable,
  placement = "bottomRight",
  children,
}: {
  channels: Channel[];
  loading?: boolean;
  /** La carga falló: es distinto de "no hay ninguno" y no hay que decir que falta permiso. */
  failed?: boolean;
  /** Qué decir cuando no hay ninguno: el motivo cambia según quién lo use. */
  emptyText?: string;
  onPick: (channelId: string) => void;
  /** Se llama al abrir, para cargar los canales recién cuando hacen falta. */
  onOpen?: () => void;
  /** Si se pasa, se muestra "Agregar canal" cuando no hay canales disponibles. */
  onAddChannel?: () => void;
  /** Ítems adicionales que siempre aparecen al final, separados por un divisor. */
  extraItems?: ExtraItem[];
  /**
   * Canales de la Cloud API que no se ofrecen porque el número no tiene la ventana abierta (D1). Se
   * nombran con el motivo: sin eso un store que sólo tiene ese canal vería "no hay canales".
   */
  unavailable?: Channel[];
  placement?: "bottomRight" | "bottomLeft";
  children: ReactElement;
}) => {
  const nota = !loading && !failed && unavailable?.length ? [notaSinVentana(unavailable)] : [];
  const empty = [
    { key: "__empty", disabled: true, label: emptyText },
    ...(onAddChannel
      ? [{
          key: "__add",
          label: (
            <Flex align="center" gap={8} style={ITEM_STYLE}>
              <PlusOutlined style={{ fontSize: ICON_SIZE }} />
              <span>Agregar canal</span>
            </Flex>
          ),
          onClick: onAddChannel,
        }]
      : []),
  ];
  // Loading y vacío se muestran como ítems deshabilitados: un desplegable que abre en blanco parece
  // roto, y el "no tenés canales con permiso" es info que el operador necesita. Si lo que falta es un
  // canal de la Cloud API por la ventana, se dice eso y no "no hay canales": el canal existe.
  const mainItems: any[] = loading
    ? [{ key: "__loading", disabled: true, label: "Cargando canales…" }]
    : failed
      ? [{ key: "__failed", disabled: true, label: "No se pudieron cargar los canales" }]
      : [
          ...(channels.length === 0 && !nota.length ? empty : []),
          ...channels.map((ch) => ({
            key: ch.channelId,
            label: (
              <Flex align="center" gap={10} style={ITEM_STYLE}>
                {channelIcon(ch.type)}
                <span>{channelLabel(ch)}</span>
              </Flex>
            ),
            onClick: () => onPick(ch.channelId),
          })),
          ...nota,
        ];

  const items: any[] = [
    ...mainItems,
    ...(extraItems?.length ? [{ type: "divider" }, ...extraItems] : []),
  ];

  return (
    <Dropdown
      trigger={["click"]}
      placement={placement}
      menu={{ items }}
      onOpenChange={(open) => {
        if (open) onOpen?.();
      }}
    >
      {children}
    </Dropdown>
  );
};

/**
 * Todos los canales que el operador puede **escribir** en ese store (WA + email + messenger), cargados
 * a demanda. Para el picker de "iniciar conversación" desde la ficha o la tabla de contactos, un pedido
 * o un presupuesto.
 *
 * Escribibles y del store: con un canal de otro store el mensaje saldría de un número que no
 * corresponde, y el hilo quedaría con el contacto en el store equivocado.
 *
 * Un canal de la Cloud API se ofrece sólo si `phone` tiene la ventana de 24 h abierta en él (D1): por
 * ahí no se le puede escribir primero a nadie. Los que se sacan vuelven en `unavailable`.
 *
 * Con `iniciaConPlantilla` se ofrece igual sin ventana: el elegido abre el modal de mensaje nuevo, que
 * crea la conversación para arrancar con una plantilla (D14), y el chat avisa lo de las 24 h. Es para
 * los que no llevan un mensaje ya escrito —contactos—; compartir un presupuesto o un pedido sí lo
 * lleva, y eso no sale sin ventana. Sin teléfono no hay a quién escribirle, y ahí no se ofrece.
 */
export function useWritableChannels(
  storeCode?: string,
  phone?: string | null,
  { iniciaConPlantilla = false }: { iniciaConPlantilla?: boolean } = {},
) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [unavailable, setUnavailable] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  /**
   * Para qué store ya se cargó bien. Se marca **al terminar con éxito**, no antes de pedir: si se
   * marcara antes, un 401 o un corte de red dejaría "no tenés canales" pegado para siempre, sin
   * reintento y sin forma de distinguirlo de una falta de permiso real.
   */
  const loadedFor = useRef<string | undefined>(undefined);
  /** Los del store sin filtrar. La ventana se vuelve a mirar en cada apertura; el listado, no. */
  const all = useRef<Channel[]>([]);

  const load = useCallback(() => {
    if (!storeCode || !isConversationsConfigured()) return;
    const loaded = loadedFor.current === storeCode;
    // La ventana de 24 h es lo único que cambia con la página abierta: sin canales de la Cloud API, lo
    // cargado sirve hasta que cambie el store.
    if (loaded && !all.current.some((c) => isCloudApi(c.type))) return;
    if (!loaded) {
      setLoading(true);
      setFailed(false);
    }
    (loaded ? Promise.resolve(all.current) : conversationsApi.listWritableChannels(storeCode))
      .then(async (chs) => {
        loadedFor.current = storeCode;
        all.current = chs;
        const offer = iniciaConPlantilla
          ? phone
            ? { channels: chs, sinVentana: [] }
            : { channels: chs.filter((c) => !isCloudApi(c.type)), sinVentana: chs.filter((c) => isCloudApi(c.type)) }
          : await ofreciblesPara(chs, phone, conversationsApi.conversacionConVentana);
        setChannels(offer.channels);
        setUnavailable(offer.sinVentana);
      })
      .catch((e) => {
        // Un fallo NO es "no tenés permiso": se distingue en pantalla y se reintenta al reabrir.
        console.error("[channel-picker] no se pudieron cargar los canales escribibles", e);
        setChannels([]);
        setUnavailable([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [storeCode, phone, iniciaConPlantilla]);

  return { channels, unavailable, loading, failed, load };
}
